/**
 * Random-access binary sources and a small little-endian cursor.
 *
 * A `ByteSource` is anything we can read byte ranges from asynchronously: a browser `Blob`/`File`
 * (sliced lazily, so a 700 MB ISO is never loaded whole), a Node file handle, or an in-memory
 * buffer. Archives sit on top of a source and themselves expose sub-sources.
 */
export interface ByteSource {
  readonly size: number;
  read(offset: number, length: number): Promise<Uint8Array>;
}

export class MemorySource implements ByteSource {
  constructor(private readonly data: Uint8Array) {}
  get size(): number {
    return this.data.length;
  }
  async read(offset: number, length: number): Promise<Uint8Array> {
    return this.data.subarray(offset, Math.min(this.data.length, offset + length));
  }
}

/** A window into another source (e.g. a file inside an ISO, an MPQ embedded in an EXE). */
export class SliceSource implements ByteSource {
  constructor(
    private readonly parent: ByteSource,
    private readonly start: number,
    readonly size: number,
  ) {}
  read(offset: number, length: number): Promise<Uint8Array> {
    const len = Math.max(0, Math.min(length, this.size - offset));
    return this.parent.read(this.start + offset, len);
  }
}

/** Browser `Blob` / `File` (also works for OPFS files). */
export class BlobSource implements ByteSource {
  constructor(readonly blob: Blob) {}
  get size(): number {
    return this.blob.size;
  }
  async read(offset: number, length: number): Promise<Uint8Array> {
    const buf = await this.blob.slice(offset, offset + length).arrayBuffer();
    return new Uint8Array(buf);
  }
}

export async function readAll(src: ByteSource): Promise<Uint8Array> {
  return src.read(0, src.size);
}

/** Little-endian cursor over a byte array. */
export class Reader {
  readonly view: DataView;
  pos: number;
  constructor(
    readonly bytes: Uint8Array,
    pos = 0,
  ) {
    this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    this.pos = pos;
  }
  get length(): number {
    return this.bytes.length;
  }
  get remaining(): number {
    return this.bytes.length - this.pos;
  }
  u8(): number {
    return this.bytes[this.pos++];
  }
  i8(): number {
    return this.view.getInt8(this.pos++);
  }
  u16(): number {
    const v = this.view.getUint16(this.pos, true);
    this.pos += 2;
    return v;
  }
  i16(): number {
    const v = this.view.getInt16(this.pos, true);
    this.pos += 2;
    return v;
  }
  u32(): number {
    const v = this.view.getUint32(this.pos, true);
    this.pos += 4;
    return v;
  }
  i32(): number {
    const v = this.view.getInt32(this.pos, true);
    this.pos += 4;
    return v;
  }
  f32(): number {
    const v = this.view.getFloat32(this.pos, true);
    this.pos += 4;
    return v;
  }
  bytesN(n: number): Uint8Array {
    const v = this.bytes.subarray(this.pos, this.pos + n);
    this.pos += n;
    return v;
  }
  skip(n: number): this {
    this.pos += n;
    return this;
  }
  /** Zero-terminated latin-1 string. */
  cstr(): string {
    let s = '';
    while (this.pos < this.bytes.length) {
      const c = this.bytes[this.pos++];
      if (c === 0) break;
      s += String.fromCharCode(c);
    }
    return s;
  }
  /** Fixed-width latin-1 string, trimmed at the first NUL. */
  fixedStr(n: number): string {
    const b = this.bytesN(n);
    let s = '';
    for (const c of b) {
      if (c === 0) break;
      s += String.fromCharCode(c);
    }
    return s;
  }
}

/** Reads `n` bits at a time, least-significant bit first (DCC, DS1-adjacent formats). */
export class BitReader {
  private bitPos: number;
  constructor(
    private readonly bytes: Uint8Array,
    startBit = 0,
  ) {
    this.bitPos = startBit;
  }
  get position(): number {
    return this.bitPos;
  }
  set position(v: number) {
    this.bitPos = v;
  }
  bits(n: number): number {
    let v = 0;
    for (let i = 0; i < n; i++) {
      const p = this.bitPos++;
      const bit = (this.bytes[p >>> 3] >>> (p & 7)) & 1;
      v |= bit << i;
    }
    return v >>> 0;
  }
  signed(n: number): number {
    if (n === 0) return 0;
    const v = this.bits(n);
    const sign = 1 << (n - 1);
    return v & sign ? v - (1 << n) : v;
  }
  bit(): number {
    const p = this.bitPos++;
    return (this.bytes[p >>> 3] >>> (p & 7)) & 1;
  }
}

export function latin1(b: Uint8Array): string {
  let s = '';
  for (let i = 0; i < b.length; i += 0x8000) {
    s += String.fromCharCode(...b.subarray(i, i + 0x8000));
  }
  return s;
}
